<script setup lang="ts">
import {
    GrowthStatistics,
    SupportProgramMarketStatistic,
    SupportProgramMarketStatisticType
} from "@/program/interfaces/Program";
import ProgramLineChartStatisticsCard from "@/program/components/program-statistics/ProgramLineChartStatisticsCard.vue";
import ProgramBinomialPieChartStatisticsCard
    from "@/program/components/program-statistics/ProgramBinomialPieChartStatisticsCard.vue";
import ProgramPieChartStatisticsCard from "@/program/components/program-statistics/ProgramPieChartStatisticsCard.vue";
import ProgramGrowthStatisticsCard from "@/program/components/program-statistics/ProgramGrowthStatisticsCard.vue";
import ProgramBarChartStatisticsCard from "@/program/components/program-statistics/ProgramBarChartStatisticsCard.vue";
import ProgramStatisticsCard from "@/program/components/program-statistics/ProgramStatisticsCard.vue";
import ProgramStatisticDialog from "@/program/components/program-statistics/dialog/ProgramStatisticDialog.vue";
import {ref} from "vue";

const {fullScreen = false, item} = defineProps<{
    supportProgramId: number;
    index: number;
    item: SupportProgramMarketStatistic;
    fullScreen?: boolean;
}>();

/**
 * Check if the item is a standard card
 * @param item - The item to check
 */
const isStandardCard = ((item: SupportProgramMarketStatistic) => {
    return [
        SupportProgramMarketStatisticType.SALE,
        SupportProgramMarketStatisticType.CONSUMER_SALE,
        SupportProgramMarketStatisticType.CONSUMER,
        SupportProgramMarketStatisticType.COVERAGE,
        SupportProgramMarketStatisticType.KPI,
        SupportProgramMarketStatisticType.COMPACT,
    ].includes(item.type);
});

const fullScreenComponent = ref<SupportProgramMarketStatistic | null>(null);

const handleDialogOpen = (value: boolean) => {
    if (!value) {
        fullScreenComponent.value = null;
    }
};

const handleFullScreenClick = (event) => {
    if(!fullScreen) {
        event.preventDefault();
        fullScreenComponent.value = item;
    }
};
</script>

<template>
  <ProgramStatisticsCard
    v-if="isStandardCard(item)"
    :data="item"
  />
  <ProgramPieChartStatisticsCard
    v-else-if="item.type === SupportProgramMarketStatisticType.PIE_CHART"
    :class="{
      'fullscreen-hover': !fullScreen
    }"
    :data="item"
    :full-screen="fullScreen"
    @click="handleFullScreenClick"
  />
  <ProgramBarChartStatisticsCard
    v-else-if="item.type === SupportProgramMarketStatisticType.BAR_CHART"
    :data="item"
    :full-screen="fullScreen"
    :class="{
      'fullscreen-hover': !fullScreen
    }"
    @click="handleFullScreenClick"
  />
  <ProgramLineChartStatisticsCard
    v-else-if="item.type ===SupportProgramMarketStatisticType.LINE_CHART"
    :data="item"
    :full-screen="fullScreen"
    :class="{
      'fullscreen-hover': !fullScreen
    }"
    @click="handleFullScreenClick"
  />
  <ProgramBinomialPieChartStatisticsCard
    v-else-if="item.type === SupportProgramMarketStatisticType.BINOMIAL_PIE_CHART"
    :data="item"
    :full-screen="fullScreen"
    :class="{
      'fullscreen-hover': !fullScreen
    }"
    @click="handleFullScreenClick"
  />
  <ProgramGrowthStatisticsCard
    v-else-if="item.type === SupportProgramMarketStatisticType.GROWTH"
    :data="item as GrowthStatistics"
  />

  <ProgramStatisticDialog v-if="!fullScreen" :support-program-id="supportProgramId" :index="index" :item="fullScreenComponent" @update:open="handleDialogOpen" />
</template>

<style scoped>
.fullscreen-hover {
    @apply cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02];
}
</style>
